import { z } from 'zod';
import { csv } from './query.js';
// The Location grammar (ADR-037 §2 · ADR-080 §8): ONE filter key, `location`,
// whose values carry their KIND — `site:<id>` · `depot:<id>` · `vehicle:<id>` —
// and a whole kind as `<kind>:*` ("All depots": only the highest pick is
// stored, so a depot attached next month is covered too). Picks add up: the
// list answers the rows at ANY of them. The read expands the tokens when it
// runs; each list says what a kind means for its rows (assets: a site is
// everything AT it — installed or in its storage). A kind joins under the same
// key without breaking a link.
export const LOCATION_KINDS = ['site', 'depot', 'vehicle'];
/** The whole kind's token value (`depot:*`). */
export const LOCATION_ALL = '*';
/** One kinded value: `<kind>:<id>` (an id of the place's own table) or `<kind>:*`. */
export const LocationTokenSchema = z
    .string()
    .max(24)
    .regex(/^(site|depot|vehicle):(\*|[A-Za-z0-9_-]{1,16})$/, 'a location is `site:<id>`, `depot:<id>`, `vehicle:<id>` or `<kind>:*`');
/** The `location` query param — comma-separated tokens, at most 50 picks. */
export const LocationParamSchema = csv(LocationTokenSchema).pipe(z.array(z.string()).max(50, 'at most 50 locations').optional());
/** The tokens read by kind — the whole kind wins over its places (they are inside it). */
export function parseLocation(tokens) {
    const out = { site: { all: false, ids: [] }, depot: { all: false, ids: [] }, vehicle: { all: false, ids: [] } };
    for (const t of tokens ?? []) {
        const at = t.indexOf(':');
        const kind = t.slice(0, at);
        const id = t.slice(at + 1);
        if (!LOCATION_KINDS.includes(kind) || !id)
            continue;
        if (id === LOCATION_ALL)
            out[kind].all = true;
        else if (!out[kind].ids.includes(id))
            out[kind].ids.push(id);
    }
    for (const k of LOCATION_KINDS)
        if (out[k].all)
            out[k].ids = [];
    return out;
}
/** The token of one place, or of a whole kind. */
export function locationToken(kind, id = LOCATION_ALL) {
    return `${kind}:${id}`;
}
